import React, { useState } from 'react';
import { Layout, Badge, Dropdown, Avatar, Space, type MenuProps } from 'antd';
import {
  BellOutlined,
  BookOutlined,
  GlobalOutlined,
  UserOutlined,
  DownOutlined,
  SettingOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useAuthStore, useUIStore } from '@/store';
import useIsMobile from '@/shared/hooks/useIsMobile';
import { UserProfileModal, UserSettingsModal } from '@/features/user-profile';
import './Header.css';
import { getInitials } from '@/features/user-management/utils';

const { Header: AntHeader } = Layout;

const Header: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { currentFunction } = useUIStore();
  const { isMobile } = useIsMobile();
  const [language, setLanguage] = useState('English');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const languageItems: MenuProps['items'] = [
    {
      key: 'en',
      label: 'English',
      onClick: () => setLanguage('English'),
    },
    {
      key: 'vi',
      label: 'Vietnamese',
      onClick: () => setLanguage('Vietnamese'),
    },
    {
      key: 'ja',
      label: 'Japanese',
      onClick: () => setLanguage('Japanese'),
    },
  ];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => setProfileModalOpen(true),
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
      onClick: () => setSettingsModalOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => logout(),
    },
  ];

  // Format role for display
  const formatRole = (role?: string) => {
    if (!role) {
      return 'User';
    }
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <>
      <AntHeader className="app-header">
        {/* Left side - System Name and Function Name */}
        <div className="app-header__left">
          <h1 className="app-header__title">STX Hub System</h1>
          {!isMobile && currentFunction && (
            <>
              <span className="app-header__divider">|</span>
              <span className="app-header__function">{currentFunction}</span>
            </>
          )}
        </div>

        {/* Right side - Actions */}
        <Space size="large" className="app-header__right">
          {/* Notifications */}
          <Badge count={3} size="small">
            <BellOutlined className="app-header__icon app-header__icon--clickable" />
          </Badge>

          {/* Manual/Help Link */}
          <BookOutlined className="app-header__icon app-header__icon--clickable" />

          {/* Language Selector */}
          <Dropdown menu={{ items: languageItems }} trigger={['click']}>
            <Space className="app-header__dropdown app-header__dropdown--clickable">
              <GlobalOutlined className="app-header__icon" />
              <span className="app-header__lang">{language}</span>
              <DownOutlined className="app-header__arrow" />
            </Space>
          </Dropdown>

          {/* Logged in User */}
          {user && (
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
              <Space className="app-header__user app-header__user--clickable">
                {!isMobile && (
                  <div className="app-header__user-info">
                    <div className="app-header__user-name">{user.userName}</div>
                    <div className="app-header__user-role">
                      {formatRole(user.role)}
                    </div>
                  </div>
                )}
                <Avatar className="app-header__avatar">
                  {getInitials(user.userName || '')}
                </Avatar>
                <DownOutlined className="app-header__arrow" />
              </Space>
            </Dropdown>
          )}
        </Space>
      </AntHeader>

      {/* Modals — only mounted after first open to avoid loading heavy deps on startup */}
      {profileModalOpen && (
        <UserProfileModal
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
        />
      )}
      {settingsModalOpen && (
        <UserSettingsModal
          open={settingsModalOpen}
          onClose={() => setSettingsModalOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
